import type { JSX } from 'preact';
import { FavoriteStar } from './FavoriteStar';
import { useMemo, useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { useWrite, todayValue } from './MoneyForms';
import { useResource } from '../lib/store';
import { authedGet } from '../lib/apiClient';
import { session } from '../lib/session';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { formatDate } from '../lib/date';
import { compareByLastName } from '../lib/personName';
import {
  SERVICE_POINTS_KEY, fetchServicePoints, addServicePoint, updateServicePoint, deleteServicePoint,
  reloadServicePoints, type ServicePointRow,
} from '../lib/servicePoints';

interface RosterPerson {
  id: number;
  full_name: string;
  file_no?: string | null;
  role: string | null;
  home_location_code: string;
  status: string | null;
}

const STAFF_ROLES = new Set(['LMT', 'EST', 'FD', 'FRONT_DESK', 'FD_MANAGER']);

function blank(clinic: string) {
  const today = todayValue();
  return {
    id: 0,
    issueDate: today,
    serviceDate: today,
    surveyNumber: 'N/A',
    submittedBy: session.value.me?.displayName || '',
    customerName: '',
    staffName: 'N/A',
    points: '',
    issueResolution: '',
    resolved: 'YES' as 'YES' | 'NO' | '',
    locationCode: clinic,
  };
}

export function ServicePoints({ clinic, pinKey }: { clinic: string; pinKey?: string }): JSX.Element {
  const data = useResource(SERVICE_POINTS_KEY, fetchServicePoints);
  const save = useWrite();
  const [form, setForm] = useState(() => blank(clinic));
  const roster = useResource<{ people: RosterPerson[] }>(
    'people-roster',
    () => authedGet('/api/people'),
  );
  const staff = useMemo(() => (
    (roster.status === 'ready' ? roster.data.people ?? [] : [])
      .filter((p) =>
        p.home_location_code === clinic
        && p.status === 'ACTIVE'
        && STAFF_ROLES.has((p.role || '').toUpperCase()))
      .sort((a, b) => compareByLastName(a.full_name, b.full_name))
  ), [roster, clinic]);

  if (data.status === 'loading') return <EmptyState label="Loading service points…" />;
  if (data.status === 'fault') return <FaultState name="could not load service points" detail={data.error} />;

  const rows = (data.data.rows ?? []).filter((r) => r.locationCode === clinic);

  function patch(next: Partial<typeof form>): void {
    setForm((prev) => ({ ...prev, ...next }));
  }

  // Owner rule 2026-09-10: every table has Add and Edit. Same startEdit
  // pattern as CompanyLicenses.tsx — prefill the one form the card already
  // has, rather than a second dialog.
  function startEdit(row: ServicePointRow): void {
    setForm({
      id: row.id,
      issueDate: row.issueDate,
      serviceDate: row.serviceDate,
      surveyNumber: row.surveyNumber,
      submittedBy: row.submittedBy,
      customerName: row.customerName,
      staffName: row.staffName,
      points: String(row.points),
      issueResolution: row.issueResolution,
      resolved: row.resolved ? 'YES' : 'NO',
      locationCode: row.locationCode,
    });
  }

  async function submit(): Promise<void> {
    const points = Number(form.points);
    await save.run(async () => {
      const body = {
        locationCode: clinic,
        issueDate: form.issueDate,
        serviceDate: form.serviceDate,
        surveyNumber: form.surveyNumber.trim() || 'N/A',
        submittedBy: form.submittedBy.trim(),
        customerName: form.customerName.trim(),
        staffName: form.staffName.trim() || 'N/A',
        points,
        issueResolution: form.issueResolution.trim(),
        resolved: form.resolved === 'YES',
      };
      if (form.id) await updateServicePoint(form.id, body);
      else await addServicePoint(body);
      setForm(blank(clinic));
      await reloadServicePoints();
    });
  }

  async function remove(row: ServicePointRow): Promise<void> {
    await save.run(async () => {
      await deleteServicePoint(row.id);
      if (form.id === row.id) setForm(blank(clinic));
      await reloadServicePoints();
    });
  }

  return (
    <div class="card" data-testid="service-points">
      <div class="section-head">
        <div class="section-head-row">
          <h2>Wufoo Form — {LOCATION_NAMES[clinic as LocationCode] ?? clinic}</h2>
          <div class="section-head-actions">{pinKey ? <FavoriteStar itemKey={pinKey} /> : null}</div>
        </div>
        <p>InMoment / guest recovery points for this clinic.</p>
        {/* Tomer 2026-09-08 (N-082): this tab is the native replacement for
            the old Wufoo form (migration 064). Each submission is saved here
            and emailed to Tomer and Candace — src/service_points_email.js. */}
        <p class="muted">
          Each submission is emailed to Tomer and Candace.
        </p>
      </div>

      <form
        class="service-point-form"
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
      >
        <label>
          Location
          <input value={LOCATION_NAMES[clinic as LocationCode] ?? clinic} readOnly />
        </label>
        <label>
          Issue Date
          <input type="date" required value={form.issueDate} onInput={(e) => patch({ issueDate: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Service Date
          <input type="date" required value={form.serviceDate} onInput={(e) => patch({ serviceDate: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Inmoment Survey Number
          <input required value={form.surveyNumber} onInput={(e) => patch({ surveyNumber: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Submitted by:
          <input required value={form.submittedBy} onInput={(e) => patch({ submittedBy: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Customer Name:
          <input required value={form.customerName} onInput={(e) => patch({ customerName: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          LMT/EST/FD
          <select
            required
            aria-label="LMT/EST/FD"
            value={form.staffName}
            onChange={(e) => patch({ staffName: (e.target as HTMLSelectElement).value })}
          >
            <option value="N/A">N/A</option>
            {staff.map((p) => (
              <option key={p.id} value={p.full_name}>
                {p.full_name} ({p.role})
              </option>
            ))}
          </select>
        </label>
        <label>
          Number of points given:
          <input type="number" min="0" step="1" required value={form.points} onInput={(e) => patch({ points: (e.target as HTMLInputElement).value })} />
        </label>
        <label class="service-point-wide">
          Issue and Resolution
          <textarea required rows={4} value={form.issueResolution} onInput={(e) => patch({ issueResolution: (e.target as HTMLTextAreaElement).value })} />
        </label>
        <label>
          Was the issue Resolved?
          <select required value={form.resolved} onChange={(e) => patch({ resolved: (e.target as HTMLSelectElement).value as 'YES' | 'NO' | '' })}>
            <option value="">Select…</option>
            <option value="YES">YES</option>
            <option value="NO">NO</option>
          </select>
        </label>
        <div class="service-point-actions">
          <button type="submit" class="btn primary" disabled={save.busy}>{form.id ? 'Save' : 'Submit'}</button>
          {form.id ? (
            <button type="button" class="btn" disabled={save.busy} onClick={() => setForm(blank(clinic))}>
              Cancel
            </button>
          ) : null}
        </div>
        {save.error ? <p class="bad" role="alert">{save.error}</p> : null}
      </form>

      {rows.length === 0 ? (
        <p class="empty">No service points filed for this clinic yet.</p>
      ) : (
        <table class="data" data-testid="service-points-table">
          <thead>
            <tr>
              <th>Issue</th>
              <th>Customer</th>
              <th>Staff</th>
              <th>Points</th>
              <th>Resolved</th>
              <th>By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{formatDate(r.issueDate)}</td>
                <td>
                  <b>{r.customerName}</b>
                  <div>{r.issueResolution}</div>
                </td>
                <td>{r.staffName}</td>
                <td>{r.points.toLocaleString('en-US')}</td>
                <td>{r.resolved ? 'YES' : 'NO'}</td>
                <td>{r.submittedBy}</td>
                <td>
                  <button type="button" class="btn" disabled={save.busy} onClick={() => startEdit(r)}>Edit</button>
                  <button type="button" class="btn" disabled={save.busy} onClick={() => { void remove(r); }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
